import React, { useState } from 'react';
import { ArrowLeft, Play, History, Trash2, Edit3, Save, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { motion } from 'motion/react';

interface GameTemplate {
  id: string;
  name: string;
  oversPerInnings: number;
  numberOfPlayers: number;
  ballsPerOver: number;
  customRules: {
    enableRetirement: boolean;
    retirementRuns: number;
    enableMaxBallsPerOver: boolean;
    maxBallsPerOver: number;
    lastOverExemption: boolean;
    enableLastManStands: boolean;
  };
  createdAt: Date;
}

interface GameHistory {
  id: string;
  teamA: string;
  teamB: string;
  finalScore: string;
  winner?: string;
  format: string;
  date: Date;
  completed: boolean;
}

interface GamesScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onStartNewGame: (template?: GameTemplate) => void;
  gameTemplates: GameTemplate[];
  gameHistory: GameHistory[];
  favoriteTemplates: string[];
  onDeleteTemplate: (id: string) => void;
  onDeleteGame: (id: string) => void;
  onToggleFavorite: (templateId: string) => void;
  onEditTemplate?: (templateId: string, updatedTemplate: Omit<GameTemplate, 'id' | 'createdAt'>) => void;
}

export function GamesScreen({
  isOpen,
  onClose,
  onStartNewGame,
  gameTemplates,
  gameHistory,
  favoriteTemplates,
  onDeleteTemplate,
  onDeleteGame,
  onToggleFavorite,
  onEditTemplate
}: GamesScreenProps) {
  const [activeTab, setActiveTab] = useState<'start' | 'history'>('start');
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);

  if (!isOpen) return null;

  const formatGameFormat = (template: GameTemplate) => {
    return `${template.oversPerInnings} overs, ${template.numberOfPlayers} players`;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5 text-blue-500" />
          </button>
          <h1 className="text-lg font-semibold">Games</h1>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('start')}
          className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
            activeTab === 'start'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Start Game
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
            activeTab === 'history'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          History
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'start' ? (
          <div className="max-w-md mx-auto space-y-4">
            {/* Quick Start */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-green-600" />
                    Quick Start
                  </CardTitle>
                  <CardDescription>
                    Start a new game with default T20 settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => onStartNewGame()}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start T20 Game
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Game Templates */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Game Templates</h3>
              {gameTemplates.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center text-gray-500">
                    <Edit3 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>No custom templates yet</p>
                    <p className="text-sm">Create templates in game settings</p>
                  </CardContent>
                </Card>
              ) : (
                gameTemplates.map((template, index) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <TemplateCard
                      template={template}
                      isEditing={editingTemplate === template.id}
                      isFavorite={favoriteTemplates.includes(template.id)}
                      onStartGame={() => onStartNewGame(template)}
                      onEdit={() => setEditingTemplate(template.id)}
                      onCancelEdit={() => setEditingTemplate(null)}
                      onSaveEdit={(updatedTemplate) => {
                        if (onEditTemplate) {
                          onEditTemplate(template.id, updatedTemplate);
                        }
                        setEditingTemplate(null);
                      }}
                      onDelete={() => onDeleteTemplate(template.id)}
                      onToggleFavorite={() => onToggleFavorite(template.id)}
                      formatGameFormat={formatGameFormat}
                    />
                  </motion.div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-md mx-auto space-y-4">
            <h3 className="font-medium text-gray-900">Game History</h3>
            {gameHistory.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-gray-500">
                  <History className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No games played yet</p>
                  <p className="text-sm">Your completed games will appear here</p>
                </CardContent>
              </Card>
            ) : (
              gameHistory.map((game, index) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">
                            {game.teamA} vs {game.teamB}
                          </CardTitle>
                          <CardDescription>
                            {formatDate(game.date)} • {game.format}
                          </CardDescription>
                        </div>
                        <button
                          onClick={() => onDeleteGame(game.id)}
                          className="text-gray-400 hover:text-red-500 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{game.finalScore}</p>
                          {game.winner && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              {game.winner} won
                            </Badge>
                          )}
                          {!game.completed && (
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                              Incomplete
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Bottom padding for navigation */}
      <div className="h-20 bg-white" />
    </div>
  );
}

function TemplateCard({
  template,
  isEditing,
  isFavorite,
  onStartGame,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onToggleFavorite,
  formatGameFormat,
}: {
  template: GameTemplate;
  isEditing: boolean;
  isFavorite: boolean;
  onStartGame: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (template: Omit<GameTemplate, 'id' | 'createdAt'>) => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
  formatGameFormat: (template: GameTemplate) => string;
}) {
  const [editForm, setEditForm] = useState({
    name: template.name,
    oversPerInnings: template.oversPerInnings,
    numberOfPlayers: template.numberOfPlayers,
    ballsPerOver: template.ballsPerOver,
    customRules: {
      enableRetirement: template.customRules.enableRetirement,
      retirementRuns: template.customRules.retirementRuns,
      enableMaxBallsPerOver: template.customRules.enableMaxBallsPerOver,
      maxBallsPerOver: template.customRules.maxBallsPerOver,
      lastOverExemption: template.customRules.lastOverExemption,
      enableLastManStands: template.customRules.enableLastManStands,
    },
  });

  const handleSave = () => {
    if (editForm.name.trim()) {
      onSaveEdit(editForm);
    }
  };

  const handleCancel = () => {
    setEditForm({
      name: template.name,
      oversPerInnings: template.oversPerInnings,
      numberOfPlayers: template.numberOfPlayers,
      ballsPerOver: template.ballsPerOver,
      customRules: {
        enableRetirement: template.customRules.enableRetirement,
        retirementRuns: template.customRules.retirementRuns,
        enableMaxBallsPerOver: template.customRules.enableMaxBallsPerOver,
        maxBallsPerOver: template.customRules.maxBallsPerOver,
        lastOverExemption: template.customRules.lastOverExemption,
        enableLastManStands: template.customRules.enableLastManStands,
      },
    });
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Edit Template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="templateName">Template Name</Label>
            <Input
              id="templateName"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              className="text-base"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Overs</Label>
              <Select
                value={editForm.oversPerInnings.toString()}
                onValueChange={(value) => 
                  setEditForm(prev => ({ ...prev, oversPerInnings: parseInt(value) }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="8">8</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Players</Label>
              <Select
                value={editForm.numberOfPlayers.toString()}
                onValueChange={(value) => 
                  setEditForm(prev => ({ ...prev, numberOfPlayers: parseInt(value) }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="8">8</SelectItem>
                  <SelectItem value="11">11</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Balls/Over</Label>
              <Select
                value={editForm.ballsPerOver.toString()}
                onValueChange={(value) => 
                  setEditForm(prev => ({ ...prev, ballsPerOver: parseInt(value) }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="8">8</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Retirement Rule</Label>
              <p className="text-xs text-gray-600">Auto-retire at run limit</p>
            </div>
            <Switch 
              checked={editForm.customRules.enableRetirement}
              onCheckedChange={(checked) => 
                setEditForm(prev => ({ 
                  ...prev, 
                  customRules: { ...prev.customRules, enableRetirement: checked }
                }))
              }
            />
          </div>

          {editForm.customRules.enableRetirement && (
            <div>
              <Label>Retirement Runs</Label>
              <Input
                type="number"
                value={editForm.customRules.retirementRuns}
                onChange={(e) => 
                  setEditForm(prev => ({ 
                    ...prev, 
                    customRules: { 
                      ...prev.customRules, 
                      retirementRuns: parseInt(e.target.value) || 25 
                    }
                  }))
                }
                min={10}
                max={200}
                className="text-base"
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Label>Max Balls per Over</Label>
              <p className="text-xs text-gray-600">Limit total balls per over</p>
            </div>
            <Switch 
              checked={editForm.customRules.enableMaxBallsPerOver}
              onCheckedChange={(checked) => 
                setEditForm(prev => ({ 
                  ...prev, 
                  customRules: { ...prev.customRules, enableMaxBallsPerOver: checked }
                }))
              }
            />
          </div>

          {editForm.customRules.enableMaxBallsPerOver && (
            <>
              <div>
                <Label>Max Balls per Over</Label>
                <Input
                  type="number"
                  value={editForm.customRules.maxBallsPerOver}
                  onChange={(e) => 
                    setEditForm(prev => ({ 
                      ...prev, 
                      customRules: { 
                        ...prev.customRules, 
                        maxBallsPerOver: parseInt(e.target.value) || 9 
                      }
                    }))
                  }
                  min={7}
                  max={15}
                  className="text-base"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Last Over Exemption</Label>
                  <p className="text-xs text-gray-600">Last over follows normal rules</p>
                </div>
                <Switch 
                  checked={editForm.customRules.lastOverExemption}
                  onCheckedChange={(checked) => 
                    setEditForm(prev => ({ 
                      ...prev, 
                      customRules: { ...prev.customRules, lastOverExemption: checked }
                    }))
                  }
                />
              </div>
            </>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Label>Last Man Stands</Label>
              <p className="text-xs text-gray-600">Allow last batter to continue alone</p>
            </div>
            <Switch 
              checked={editForm.customRules.enableLastManStands}
              onCheckedChange={(checked) => 
                setEditForm(prev => ({ 
                  ...prev, 
                  customRules: { ...prev.customRules, enableLastManStands: checked }
                }))
              }
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={!editForm.name.trim()} className="flex-1">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={handleCancel} className="flex-1">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative group">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-8">
            <CardTitle className="text-base">{template.name}</CardTitle>
            <CardDescription>{formatGameFormat(template)}</CardDescription>
          </div>
          <div className="flex items-center gap-1">
            {/* Favorite star */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite();
              }}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Star 
                className={`w-4 h-4 ${
                  isFavorite 
                    ? 'text-yellow-500 fill-yellow-500' 
                    : 'text-gray-400 hover:text-yellow-500'
                }`} 
              />
            </motion.button>
            <button
              onClick={onEdit}
              className="text-gray-400 hover:text-blue-500 p-1"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="text-gray-400 hover:text-red-500 p-1"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline">{template.ballsPerOver} balls/over</Badge>
          {template.customRules.enableRetirement && (
            <Badge variant="outline" className="text-purple-600 border-purple-600">
              Retirement @ {template.customRules.retirementRuns}
            </Badge>
          )}
          {template.customRules.enableLastManStands && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              Last Man Stands
            </Badge>
          )}
          {template.customRules.enableMaxBallsPerOver && (
            <Badge variant="outline" className="text-orange-600 border-orange-600">
              Max {template.customRules.maxBallsPerOver} balls/over
            </Badge>
          )}
        </div>
        <Button
          onClick={onStartGame}
          className="w-full"
          variant="outline"
        >
          <Play className="w-4 h-4 mr-2" />
          Start Game
        </Button>
      </CardContent>
    </Card>
  );
}